import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
    Bell,
    BellOff,
    ArrowLeft,
    Smartphone,
    Monitor,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    Send,
} from 'lucide-react';

export default function NotificationsConfigPage() {
    const navigate = useNavigate();
    const {
        isSupported,
        isSubscribed,
        permission,
        loading,
        error,
        subscribe,
        unsubscribe,
        sendTestNotification
    } = usePushNotifications();
    
    const [sendingTest, setSendingTest] = useState(false);

    const handleToggle = async () => {
        if (isSubscribed) {
            const success = await unsubscribe();
            if (success) {
                toast.success('Notificaciones desactivadas');
            } else {
                toast.error('Error al desactivar notificaciones');
            }
        } else {
            const success = await subscribe();
            if (success) {
                toast.success('Notificaciones activadas');
            } else {
                toast.error(error || 'Error al activar notificaciones');
            }
        }
    };

    const handleSendTest = async () => {
        setSendingTest(true);
        try {
            const result = await sendTestNotification();
            toast.success(`Notificación enviada a ${result.sent} dispositivo(s)`);
        } catch (err) {
            toast.error('Error al enviar notificación de prueba');
        } finally {
            setSendingTest(false);
        }
    };

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Bell className="h-6 w-6" />
                        Notificaciones Push
                    </h1>
                    <p className="text-muted-foreground">Configura las notificaciones en tiempo real</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Estado y Activación */}
                <Card>
                    <CardHeader>
                        <CardTitle>Estado de Notificaciones</CardTitle>
                        <CardDescription>
                            Recibe alertas instantáneas sobre nuevas órdenes y cambios de estado
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {!isSupported ? (
                            <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <AlertCircle className="h-5 w-5 text-yellow-600" />
                                <div>
                                    <p className="font-medium text-yellow-800">No soportado</p>
                                    <p className="text-sm text-yellow-700">
                                        Tu navegador no soporta notificaciones push
                                    </p>
                                </div>
                            </div>
                        ) : permission === 'denied' ? (
                            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <XCircle className="h-5 w-5 text-red-600" />
                                <div>
                                    <p className="font-medium text-red-800">Bloqueado</p>
                                    <p className="text-sm text-red-700">
                                        Has bloqueado las notificaciones. Debes habilitarlas en la configuración del navegador.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {isSubscribed ? (
                                            <div className="p-2 bg-green-100 rounded-full">
                                                <Bell className="h-5 w-5 text-green-600" />
                                            </div>
                                        ) : (
                                            <div className="p-2 bg-gray-100 rounded-full">
                                                <BellOff className="h-5 w-5 text-gray-500" />
                                            </div>
                                        )}
                                        <div>
                                            <Label className="text-base">Notificaciones Push</Label>
                                            <p className="text-sm text-muted-foreground">
                                                {isSubscribed ? 'Recibirás alertas en tiempo real' : 'Activar para recibir alertas'}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={isSubscribed}
                                        onCheckedChange={handleToggle}
                                        disabled={loading}
                                    />
                                </div>

                                {isSubscribed && (
                                    <div className="pt-4 border-t">
                                        <Button
                                            variant="outline"
                                            onClick={handleSendTest}
                                            disabled={sendingTest}
                                            className="w-full"
                                        >
                                            {sendingTest ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Send className="h-4 w-4 mr-2" />
                                            )}
                                            Enviar Notificación de Prueba
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Tipos de notificaciones */}
                <Card>
                    <CardHeader>
                        <CardTitle>Tipos de Alertas</CardTitle>
                        <CardDescription>
                            Recibirás notificaciones para estos eventos
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium">Nueva Orden Asignada</p>
                                    <p className="text-sm text-muted-foreground">
                                        Cuando se te asigne una nueva orden de servicio
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium">Orden Completada</p>
                                    <p className="text-sm text-muted-foreground">
                                        Cuando un técnico complete una orden
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium">Cambio de Estado</p>
                                    <p className="text-sm text-muted-foreground">
                                        Cuando cambie el estado de una orden
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                <div>
                                    <p className="font-medium">Cliente Completó Datos</p>
                                    <p className="text-sm text-muted-foreground">
                                        Cuando un cliente complete información pendiente
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info dispositivos */}
            <Card>
                <CardHeader>
                    <CardTitle>Dispositivos Compatibles</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-4 border rounded-lg">
                            <Monitor className="h-8 w-8 text-blue-500" />
                            <div>
                                <p className="font-medium">Navegador Web</p>
                                <p className="text-sm text-muted-foreground">
                                    Chrome, Firefox, Edge, Safari
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-4 border rounded-lg">
                            <Smartphone className="h-8 w-8 text-green-500" />
                            <div>
                                <p className="font-medium">Dispositivos Móviles</p>
                                <p className="text-sm text-muted-foreground">
                                    Android (Chrome), iOS (Safari 16.4+)
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
